
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { DownloadIcon } from '@/components/Icons';

// Definimos las propiedades que el componente aceptará
interface DataTableExportProps<TData> {
  data: TData[];
  columns: Array<{ id: keyof TData; name: string }>;
  fileName?: string;
}

export function DataTableExport<TData>({ 
    data, 
    columns,
    fileName = 'export' 
}: DataTableExportProps<TData>) {
  // Estado para las columnas seleccionadas, inicialmente todas marcadas
  const [selectedColumns, setSelectedColumns] = useState<Array<keyof TData>>(() => 
    columns.map(c => c.id)
  );

  // Maneja el cambio en los checkboxes
  const handleColumnToggle = (columnId: keyof TData) => {
    setSelectedColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  // Función que se ejecuta al pulsar el botón de exportar
  const handleExport = () => {
    // Filtramos los datos para incluir solo las columnas seleccionadas
    const filteredData = data.map(row => {
      const newRow: { [key: string]: any } = {};
      selectedColumns.forEach(colId => {
        // Buscamos el nombre legible de la columna para usarlo como cabecera
        const column = columns.find(c => c.id === colId);
        if (column) {
            newRow[column.name] = row[colId];
        }
      });
      return newRow;
    });

    // Creamos la hoja de cálculo y el libro
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

    // Generamos y descargamos el fichero Excel
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <DownloadIcon className="mr-2 h-4 w-4" />
          Exportar a Excel
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configurar Exportación</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          {columns.map(column => (
            <div key={column.id as string} className="flex items-center space-x-2">
              <Checkbox
                id={`col-${column.id as string}`}
                checked={selectedColumns.includes(column.id)}
                onCheckedChange={() => handleColumnToggle(column.id)}
              />
              <Label htmlFor={`col-${column.id as string}`} className="font-medium">
                {column.name}
              </Label>
            </div>
          ))}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancelar</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button onClick={handleExport}>Exportar</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
